import {
    handleSessionEnd,
    touchUsuarioActivity,
} from '../sessionEnd';

// URLs de la API - Cardiología Chaco
const localApiUrl = "http://ch-cardiologia.test/api/v1/";
const productionApiUrl = ""; // Opcional: otra URL de producción (ej: "https://api.ejemplo.gob.ar/api/v1/")

// Subdominios en vivo (digitalsis.com.ar): frontend y API en subdominios distintos
const PRODUCTION_FRONTEND_HOST = "cardiologiachaco.digitalsis.com.ar";
const PRODUCTION_API_BASE = "https://cardiologiachacoapi.digitalsis.com.ar/api/v1/";

export function serverPath(){
    const hostname = window.location.hostname;
    
    // Desarrollo local: si estamos en localhost o 127.0.0.1 (Vite dev server), usar URL completa
    if(hostname === "localhost" || hostname === "127.0.0.1") {
        return localApiUrl;
    }
    
    // Producción conocida: frontend en cardiologiachaco → API en cardiologiachacoapi
    if(hostname === PRODUCTION_FRONTEND_HOST) {
        return PRODUCTION_API_BASE;
    }
    
    // Desarrollo local: si estamos en un dominio .test, usar URL relativa si es el mismo origen
    if(hostname.includes(".test")) {
        const currentOrigin = window.location.origin;
        if(currentOrigin !== `http://${hostname}` && currentOrigin !== `https://${hostname}`) {
            return localApiUrl;
        }
        return "/api/v1/";
    }
    
    // Otra producción: usar la URL configurada
    if(productionApiUrl) {
        return productionApiUrl;
    }
    
    return "/api/v1/";
}

function applySessionEndFromError(errBody, status, requestName) {
    const code = errBody?.code || null;
    const message = errBody?.error?.message || errBody?.error || errBody?.message || null;
    return handleSessionEnd({ status, code, message, requestName });
}

function throwHttpError(errorMsg, status, code) {
    const error = new Error(errorMsg);
    error.status = status;
    if (code) error.code = code;
    throw error;
}

function parseJsonErrorMessage(err, status) {
    if (status === 422) {
        if (err.errors && typeof err.errors === 'object') {
            const fromErrors = Object.entries(err.errors)
                .map(([, messages]) => (Array.isArray(messages) ? messages.join(', ') : messages))
                .filter(Boolean)
                .join('. ');
            if (fromErrors) return fromErrors;
        }
        if (typeof err.error === 'string' && err.error) return err.error;
        if (err.error?.message) return err.error.message;
        if (err.message && err.message !== 'The given data was invalid.') return err.message;
        return 'Error de validación';
    }
    return err.error?.message || err.error || err.message || 'Error en la petición';
}

async function rejectUnauthorized(res, requestName) {
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        try {
            const err = await res.json();
            const errorMsg = parseJsonErrorMessage(err, res.status);
            const sessionMsg = applySessionEndFromError(err, res.status, requestName);
            throwHttpError(sessionMsg || errorMsg, res.status, err.code);
        } catch (e) {
            if (e.status) throw e;
            const sessionMsg = handleSessionEnd({ status: res.status, requestName });
            throwHttpError(sessionMsg || `Error ${res.status}: ${res.statusText}`, res.status);
        }
    }
    const sessionMsg = handleSessionEnd({ status: res.status, requestName });
    throwHttpError(sessionMsg || `Error ${res.status}: ${res.statusText}`, res.status);
}

function normalizeCatchError(error, requestName) {
    let t_error = error.message;
    const sessionMsg = handleSessionEnd({
        status: error.status,
        code: error.code,
        message: t_error,
        requestName,
    });
    if (sessionMsg) {
        return sessionMsg;
    }
    if (t_error === 'Failed to fetch') {
        return 'Error al conectar con el servidor';
    }
    return t_error;
}

function markActivityOnSuccess() {
    touchUsuarioActivity();
}

/** Headers JSON + Accept para que Laravel no redirija errores de validación al frontend (CORS). */
function jsonHeaders(token = null, extra = {}) {
    const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...extra,
    };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    return headers;
}

export async function postData(name = '', data = {}, token = null) {
    const response = { data: {}, error: "", warning: "", filename: "" };

    const headers = jsonHeaders(token);

    await fetch(serverPath() + name, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(data)
    })
        .then(async (res) => {
            var t_contentDisposition = res.headers.get("content-disposition");
            if(t_contentDisposition!=null && t_contentDisposition.includes("attachment")){
                response.filename = t_contentDisposition.split(";")[1].split('=')[1];
                return res.blob();
            }

            if (res.status === 204) {
                return null;
            }

            if (!res.ok) {
                await rejectUnauthorized(res, name);
            }

            const contentType = res.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return res.json();
            }
            return null;
        })
        .then((data) => {
            response.data = data?.data != null ? data.data : (data ? data : {});
            response.error = data?.error ? data.error : "";
            response.warning = data?.warning ? data.warning : "";
            if (!response.error) markActivityOnSuccess();
        })
        .catch((error) => {
            response.error = normalizeCatchError(error, name);
        });

    return response;
}

export async function getData(name = '', token = null) {
    const response = { data: {}, error: "", warning: "" };

    const headers = jsonHeaders(token);

    await fetch(serverPath() + name, {
        method: 'GET',
        headers: headers,
    })
        .then(async (res) => {
            if (!res.ok) {
                await rejectUnauthorized(res, name);
            }
            return res.json();
        })
        .then((data) => {
            response.data = data.data ? data.data : (data ? data : {});
            response.error = data.error ? data.error : "";
            response.warning = data.warning ? data.warning : "";
            if (!response.error) markActivityOnSuccess();
        })
        .catch((error) => {
            response.error = normalizeCatchError(error, name);
        });

    return response;
}

export async function getDataFull(name = '', token = null) {
    const response = {
        data: {},
        error: "",
        warning: "",
        meta: {},
        counts: null,
        unread_count: null,
        poll_hint_seconds: null,
        raw: null,
    };

    const headers = jsonHeaders(token);

    await fetch(serverPath() + name, {
        method: 'GET',
        headers: headers,
    })
        .then(async (res) => {
            if (!res.ok) {
                await rejectUnauthorized(res, name);
            }

            const contentType = res.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return res.json();
            }
            return null;
        })
        .then((data) => {
            response.raw = data;
            response.data = data?.data != null ? data.data : (data ? data : {});
            response.meta = data?.meta ? data.meta : {};
            response.counts = data?.counts != null ? data.counts : null;
            response.unread_count = data?.unread_count != null ? data.unread_count : null;
            response.poll_hint_seconds = data?.poll_hint_seconds != null ? data.poll_hint_seconds : null;
            response.error = data?.error ? data.error : "";
            response.warning = data?.warning ? data.warning : "";
            if (!response.error) markActivityOnSuccess();
        })
        .catch((error) => {
            response.error = normalizeCatchError(error, name);
        });

    return response;
}

/** Descarga un recurso GET como archivo (p. ej. CSV de auditoría). */
export async function downloadGet(name = '', token = null, fallbackFilename = 'export.csv') {
    const headers = jsonHeaders(token);
    delete headers['Content-Type'];

    try {
        const res = await fetch(serverPath() + name, { method: 'GET', headers });
        if (!res.ok) {
            await rejectUnauthorized(res, name);
        }
        const blob = await res.blob();
        const cd = res.headers.get('content-disposition');
        let filename = fallbackFilename;
        if (cd && cd.includes('filename=')) {
            filename = cd.split('filename=')[1].replace(/["']/g, '').trim();
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        markActivityOnSuccess();
        return { error: '' };
    } catch (error) {
        return { error: normalizeCatchError(error, name) };
    }
}

export async function putData(name = '', data = {}, token = null) {
    const response = { data: {}, error: "", warning: "" };

    const headers = jsonHeaders(token);

    await fetch(serverPath() + name, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify(data)
    })
        .then(async (res) => {
            if (res.status === 204) {
                return null;
            }

            if (!res.ok) {
                await rejectUnauthorized(res, name);
            }

            const contentType = res.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return res.json();
            }
            return null;
        })
        .then((data) => {
            response.data = data?.data != null ? data.data : (data ? data : {});
            response.error = data?.error ? data.error : "";
            response.warning = data?.warning ? data.warning : "";
            if (!response.error) markActivityOnSuccess();
        })
        .catch((error) => {
            response.error = normalizeCatchError(error, name);
        });

    return response;
}

export async function deleteData(name = '', token = null) {
    const response = { data: {}, error: "", warning: "" };

    const headers = jsonHeaders(token);

    await fetch(serverPath() + name, {
        method: 'DELETE',
        headers: headers,
    })
        .then(async (res) => {
            if (res.status === 204) {
                return null;
            }

            if (!res.ok) {
                await rejectUnauthorized(res, name);
            }

            const contentType = res.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return res.json();
            }
            return null;
        })
        .then((data) => {
            response.data = data?.data ? data.data : (data ? data : {});
            response.error = data?.error ? data.error : "";
            response.warning = data?.warning ? data.warning : "";
            if (!response.error) markActivityOnSuccess();
        })
        .catch((error) => {
            response.error = normalizeCatchError(error, name);
        });

    return response;
}

export async function postFiles(name = '', data = {}, files = {}, token = null) {
    const response = { data: {}, error: "", warning: "" };

    const keysData = Object.keys(data);
    const keysFiles = Object.keys(files);
    const formData = new FormData();
    
    for (var i = 0; i < keysData.length; i++) {
        formData.append(keysData[i], data[keysData[i]]);
    }
    for (i = 0; i < keysFiles.length; i++) {
        for (var i2 = 0; i2 < files[keysFiles[i]].length; i2++) {
            if (files[keysFiles[i]][i2].file !== null)
                formData.append(keysFiles[i] + "_" + i2, files[keysFiles[i]][i2].file);
        }
        formData.append(keysFiles[i], JSON.stringify(files[keysFiles[i]]));
    }

    const headers = { Accept: 'application/json' };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    await fetch(serverPath() + name, {
        method: 'POST',
        headers: headers,
        body: formData
    })
        .then(async (res) => {
            if (!res.ok) {
                await rejectUnauthorized(res, name);
            }
            return res.json();
        })
        .then((data) => {
            response.data = data.data ? data.data : [];
            response.error = data.error ? data.error : "";
            response.warning = data.warning ? data.warning : "";
            if (!response.error) markActivityOnSuccess();
        })
        .catch((error) => {
            response.error = normalizeCatchError(error, name);
        });

    return response;
}
